import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Snackbar
} from '@mui/material';
import { Search as SearchIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import PageMeta from '../../../components/PageMeta';
import { searchClaimableCustomer, claimCustomer } from '../../../services/marketing';

const ManagerClaim = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const showNotification = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await searchClaimableCustomer(searchQuery);
      setResult(data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to search customer');
      // If 404, we might want to show specific message
      if (err.response?.status === 404) {
          setError(err.response.data.error || 'Customer not found or not eligible');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!result?.customer?.username) return;

    setClaimLoading(true);
    try {
      const response = await claimCustomer(result.customer.username);
      showNotification(response.message || 'Customer claimed successfully', 'success');
      
      // Refresh data
      const data = await searchClaimableCustomer(searchQuery);
      setResult(data);
    } catch (err) {
      console.error(err);
      const details = err.response?.data?.details;
      const suffixParts = [];
      if (details?.stage) suffixParts.push(`stage: ${details.stage}`);
      if (details?.manager) suffixParts.push(`manager: ${details.manager}`);
      const suffix = suffixParts.length ? ` (${suffixParts.join(', ')})` : '';
      showNotification((err.response?.data?.error || 'Failed to claim customer') + suffix, 'error');
    } finally {
      setClaimLoading(false);
    }
  };

  const getStatusColor = (stage) => {
    switch (stage) {
      case 'unassigned': return 'warning';
      case 'claimed': return 'success';
      case 'auto_assigned': return 'info';
      case 'admin_assigned': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <PageMeta title="Manager Claim | Marketing" />
      
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Manager Claim Customer
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Search for customers by email or phone to claim them.
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <form onSubmit={handleSearch}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label="Search Customer (Email or Phone)"
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter email or phone number"
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  disabled={loading || !searchQuery.trim()}
                  sx={{ height: 56 }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Customer Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{result.customer.full_name || 'N/A'}</Typography>
                </Box>
                
                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{result.customer.email}</Typography>
                </Box>
                
                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{result.customer.phone || 'N/A'}</Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary">Joined</Typography>
                  <Typography variant="body1">
                    {result.customer.date_joined
                      ? new Date(result.customer.date_joined).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>
                  Assignment Status
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box mb={2}>
                  <Typography variant="subtitle2" color="textSecondary">Current Stage</Typography>
                  <Chip 
                    label={result.assignment.stage.toUpperCase()} 
                    color={getStatusColor(result.assignment.stage)} 
                    size="small" 
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                {result.assignment.manager && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary">Assigned Manager</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {result.assignment.manager}
                    </Typography>
                  </Box>
                )}

                {result.assignment.auto_assign_after && (
                   <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary">Auto Assign After</Typography>
                    <Typography variant="body1">
                      {new Date(result.assignment.auto_assign_after).toLocaleString()}
                    </Typography>
                  </Box>
                )}

                <Box mt={4}>
                  {result.eligibility.is_eligible ? (
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      fullWidth
                      startIcon={claimLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                      onClick={handleClaim}
                      disabled={claimLoading}
                    >
                      {claimLoading ? 'Claiming...' : 'Claim Customer'}
                    </Button>
                  ) : (
                    result.assignment?.is_assigned_to_me ? (
                      <Alert severity="success">
                        You have successfully claimed this customer and they are linked to you.
                      </Alert>
                    ) : (
                      <Alert severity="warning">
                        Cannot claim: {result.eligibility.reasons.join(', ')}
                      </Alert>
                    )
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManagerClaim;
